import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { BodyMapView } from './body-map.types';

export interface BodyMap3dTap {
  x: number;
  y: number;
  z: number;
  nx: number;
  ny: number;
  nz: number;
  cameraZ: number;
}

export class BodyMap3dCanvas {
  private readonly backgroundColor = 0xf7f8fb;
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer = new THREE.Vector2();
  private readonly loader = new GLTFLoader();

  private container: HTMLElement | null = null;
  private scene: THREE.Scene | null = null;
  private camera: THREE.PerspectiveCamera | null = null;
  private renderer: THREE.WebGLRenderer | null = null;
  private controls: OrbitControls | null = null;
  private model: THREE.Object3D | null = null;
  private selectionMarker: THREE.Mesh | null = null;
  private markerScale = 0.08;
  private cameraDistance = 30;
  private animationId: number | null = null;
  private pointerDown: { x: number; y: number; time: number } | null = null;
  private isDragging = false;
  private resizeObserver: ResizeObserver | null = null;
  private currentModelUrl: string | null = null;
  private currentView: BodyMapView = 'FRONT';
  private loadGeneration = 0;
  private markerPoint: THREE.Vector3 | null = null;
  private interactive = true;

  onTap: ((tap: BodyMap3dTap) => void) | null = null;
  onReady: (() => void) | null = null;
  onError: ((message: string) => void) | null = null;

  get isReady(): boolean {
    return !!this.model && !!this.renderer;
  }

  mount(container: HTMLElement): void {
    this.dispose();
    this.container = container;

    const width = Math.max(container.clientWidth, 1);
    const height = Math.max(container.clientHeight, 1);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(this.backgroundColor);

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.set(0, 5, 45);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    container.appendChild(renderer.domElement);

    scene.add(new THREE.AmbientLight(0xffffff, 0.85));
    const hemi = new THREE.HemisphereLight(0xffffff, 0xc8cdd4, 0.55);
    scene.add(hemi);
    const key = new THREE.DirectionalLight(0xffffff, 0.55);
    key.position.set(6, 16, 12);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xffffff, 0.55);
    fill.position.set(-6, 16, -12);
    scene.add(fill);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = 4;
    controls.maxDistance = 60;
    controls.target.set(0, 3, 0);
    controls.update();

    this.scene = scene;
    this.camera = camera;
    this.renderer = renderer;
    this.controls = controls;

    renderer.domElement.addEventListener('pointerdown', this.onPointerDown);
    renderer.domElement.addEventListener('pointermove', this.onPointerMove);
    renderer.domElement.addEventListener('pointerup', this.onPointerUp);
    renderer.domElement.addEventListener('pointercancel', this.onPointerUp);

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(container);
    }

    this.animate();
  }

  loadModel(url: string): void {
    if (!this.scene || (this.currentModelUrl === url && this.model)) {
      return;
    }
    this.currentModelUrl = url;
    const generation = ++this.loadGeneration;
    this.loader.load(
      url,
      (gltf) => {
        if (generation !== this.loadGeneration) {
          return;
        }
        this.applyLoadedGltf(gltf.scene);
        this.onReady?.();
      },
      undefined,
      (error) => {
        if (generation !== this.loadGeneration) {
          return;
        }
        const message =
          error instanceof Error
            ? error.message
            : typeof error === 'object' &&
                error &&
                'message' in error &&
                typeof (error as { message: unknown }).message === 'string'
              ? (error as { message: string }).message
              : 'Failed to load model';
        this.onError?.(message);
      },
    );
  }

  setCameraView(view: BodyMapView): void {
    this.currentView = view;
    this.applyCameraView();
  }

  setInteractive(interactive: boolean): void {
    this.interactive = interactive;
  }

  setSelectionMarker(point: { x: number; y: number; z: number } | null): void {
    this.markerPoint = point
      ? new THREE.Vector3(point.x, point.y, point.z)
      : null;
    this.applySelectionMarker();
  }

  private applyCameraView(): void {
    if (!this.camera || !this.controls) {
      return;
    }
    const yaw = this.currentView === 'BACK' ? Math.PI : 0;
    const elevation = 0.12;
    const x = Math.sin(yaw) * this.cameraDistance;
    const z = Math.cos(yaw) * this.cameraDistance;
    const y = this.cameraDistance * elevation;
    this.camera.position.set(x, y, z);
    this.controls.target.set(0, 0, 0);
    this.lockVerticalOrbit();
  }

  clearSelectionMarker(): void {
    this.markerPoint = null;
    this.removeSelectionMarker();
  }

  private removeSelectionMarker(): void {
    if (!this.selectionMarker || !this.model) {
      this.selectionMarker = null;
      return;
    }
    this.model.remove(this.selectionMarker);
    this.selectionMarker.geometry.dispose();
    const material = this.selectionMarker.material;
    if (Array.isArray(material)) {
      material.forEach((item) => item.dispose());
    } else {
      material.dispose();
    }
    this.selectionMarker = null;
  }

  dispose(): void {
    this.loadGeneration += 1;
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.animationId != null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    if (this.renderer) {
      this.renderer.domElement.removeEventListener(
        'pointerdown',
        this.onPointerDown,
      );
      this.renderer.domElement.removeEventListener(
        'pointermove',
        this.onPointerMove,
      );
      this.renderer.domElement.removeEventListener(
        'pointerup',
        this.onPointerUp,
      );
      this.renderer.domElement.removeEventListener(
        'pointercancel',
        this.onPointerUp,
      );
      this.renderer.dispose();
      this.renderer.domElement.remove();
    }
    this.removeSelectionMarker();
    this.disposeObject(this.model);
    this.controls?.dispose();
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    this.model = null;
    this.container = null;
    this.currentModelUrl = null;
    this.pointerDown = null;
    this.isDragging = false;
  }

  private applyLoadedGltf(root: THREE.Group): void {
    if (!this.scene) {
      return;
    }
    if (this.model) {
      this.removeSelectionMarker();
      this.scene.remove(this.model);
      this.disposeObject(this.model);
      this.model = null;
    }

    this.model = root;
    this.scene.add(root);

    root.traverse((child) => {
      if (!(child instanceof THREE.Mesh) || !child.material) {
        return;
      }
      const materials = Array.isArray(child.material)
        ? child.material
        : [child.material];
      materials.forEach((mat) => {
        if (mat instanceof THREE.MeshStandardMaterial) {
          mat.color.setRGB(1, 1, 1);
          mat.roughness = 0.9;
          mat.metalness = 0.0;
          mat.emissive.setRGB(0.12, 0.12, 0.12);
          mat.needsUpdate = true;
        }
      });
    });

    this.frameModel();
    this.applySelectionMarker();
  }

  private frameModel(): void {
    if (!this.model || !this.camera || !this.controls || !this.renderer) {
      return;
    }
    const container = this.container;
    this.model.position.set(0, 0, 0);
    this.model.updateMatrixWorld(true);

    const box = new THREE.Box3().setFromObject(this.model);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    this.model.position.sub(center);

    const maxDim = Math.max(size.x, size.y, size.z);
    this.markerScale = maxDim * 0.012;

    const width = container?.clientWidth || this.renderer.domElement.clientWidth;
    const height =
      container?.clientHeight || this.renderer.domElement.clientHeight;
    if (width > 0 && height > 0) {
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(width, height);
    }

    const vFov = (this.camera.fov * Math.PI) / 180;
    const aspect = this.camera.aspect > 0 ? this.camera.aspect : 1;
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect);
    const distForHeight = size.y / (2 * Math.tan(vFov / 2));
    const distForWidth = size.x / (2 * Math.tan(hFov / 2));
    this.cameraDistance = Math.max(distForHeight, distForWidth, maxDim) * 1.05;

    this.camera.position.set(0, maxDim * 0.12, this.cameraDistance);
    this.controls.target.set(0, 0, 0);
    this.controls.minDistance = this.cameraDistance * 0.35;
    this.controls.maxDistance = this.cameraDistance * 3;
    this.applyCameraView();
  }

  private resize = (): void => {
    if (!this.renderer || !this.camera || !this.container) {
      return;
    }
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;
    if (width <= 0 || height <= 0) {
      return;
    }
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
    if (this.model) {
      this.frameModel();
    }
  };

  private animate = (): void => {
    this.animationId = requestAnimationFrame(this.animate);
    this.controls?.update();
    if (this.renderer && this.scene && this.camera) {
      this.renderer.render(this.scene, this.camera);
    }
  };

  private lockVerticalOrbit(): void {
    if (!this.controls) {
      return;
    }
    this.controls.update();
    const polar = this.controls.getPolarAngle();
    this.controls.minPolarAngle = polar;
    this.controls.maxPolarAngle = polar;
  }

  private showSelectionMarker(localPoint: THREE.Vector3): void {
    this.removeSelectionMarker();
    if (!this.model) {
      return;
    }
    const geometry = new THREE.SphereGeometry(this.markerScale, 16, 16);
    const material = new THREE.MeshStandardMaterial({
      color: 0x2f63ff,
      emissive: 0x2f63ff,
      emissiveIntensity: 0.35,
    });
    this.selectionMarker = new THREE.Mesh(geometry, material);
    this.selectionMarker.position.copy(localPoint);
    this.model.add(this.selectionMarker);
  }

  private applySelectionMarker(): void {
    if (!this.markerPoint || !this.model) {
      this.removeSelectionMarker();
      return;
    }
    this.showSelectionMarker(this.markerPoint);
  }

  private onPointerDown = (event: PointerEvent): void => {
    this.pointerDown = { x: event.clientX, y: event.clientY, time: Date.now() };
    this.isDragging = false;
  };

  private onPointerMove = (event: PointerEvent): void => {
    if (!this.pointerDown) {
      return;
    }
    const dx = event.clientX - this.pointerDown.x;
    const dy = event.clientY - this.pointerDown.y;
    if (Math.hypot(dx, dy) > 8) {
      this.isDragging = true;
    }
  };

  private onPointerUp = (event: PointerEvent): void => {
    if (!this.pointerDown || !this.model || !this.renderer) {
      this.pointerDown = null;
      return;
    }
    const elapsed = Date.now() - this.pointerDown.time;
    const dx = event.clientX - this.pointerDown.x;
    const dy = event.clientY - this.pointerDown.y;
    const moved = Math.hypot(dx, dy);
    this.pointerDown = null;
    if (this.isDragging || moved > 10 || elapsed > 450) {
      this.isDragging = false;
      return;
    }
    this.isDragging = false;
    this.handleTap(event.clientX, event.clientY);
  };

  private handleTap(clientX: number, clientY: number): void {
    if (!this.interactive || !this.renderer || !this.camera || !this.model) {
      return;
    }
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointer.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const intersects = this.raycaster.intersectObject(this.model, true);
    if (!intersects.length) {
      return;
    }
    const hit = intersects[0];
    const localPoint = this.model.worldToLocal(hit.point.clone());
    const normal = hit.face
      ? hit.face.normal
          .clone()
          .transformDirection(hit.object.matrixWorld)
          .normalize()
      : new THREE.Vector3(0, 0, 1);

    this.showSelectionMarker(localPoint);
    this.onTap?.({
      x: localPoint.x,
      y: localPoint.y,
      z: localPoint.z,
      nx: normal.x,
      ny: normal.y,
      nz: normal.z,
      cameraZ: this.camera.position.z,
    });
  }

  private disposeObject(object: THREE.Object3D | null): void {
    if (!object) {
      return;
    }
    object.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) {
        return;
      }
      child.geometry?.dispose();
      const material = child.material;
      if (Array.isArray(material)) {
        material.forEach((item) => item.dispose());
      } else {
        material?.dispose();
      }
    });
  }
}
